import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  InputAdornment,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

// Validation schema
const gradeValidationSchema = Yup.object({
  grade: Yup.string().required('Grade is required'), //[O,A+,A,B+,B,C,D,E,F]
  // .matches(
  //   /^[A-F][+-]?$/,
  //   'Grade must be valid format (e.g., A+, A, B-, etc.)',
  // )
  //.oneOf(['O', 'A+', 'A', 'B+', 'B', 'C', 'D', 'E', 'F'], 'upto F only')
  rangeFrom: Yup.number()
    .required('Minimum percentage is required')
    .min(0, 'Minimum percentage must be at least 0')
    .max(100, 'Minimum percentage cannot exceed 100')
    .test(
      'min-less-than-max',
      'Minimum percentage must be less than maximum percentage',
      function (value) {
        const { rangeTo } = this.parent;
        return !rangeTo || value < rangeTo;
      },
    ),
  rangeTo: Yup.number()
    .required('Maximum percentage is required')
    .min(0, 'Maximum percentage must be at least 0')
    .max(100, 'Maximum percentage cannot exceed 100')
    .test(
      'max-greater-than-min',
      'Maximum percentage must be greater than minimum percentage',
      function (value) {
        const { rangeFrom } = this.parent;
        return !rangeFrom || value > rangeFrom;
      },
    ),
});

const initialFormValues = {
  grade: '',
  rangeFrom: '',
  rangeTo: '',
};

const GradeFormDialog = ({
  open,
  onClose,
  onSubmit,
  mode, // 'add' or 'edit'
  initialData,
  loading = false,
}) => {
  const isEditMode = mode === 'edit';
  const title = isEditMode ? 'Edit Grade' : 'Add New Grade';

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      await onSubmit(values);
      onClose();
    } catch (error) {
      console.error('Error submitting grade:', error);

      // Handle specific field errors
      if (error.response?.data?.fieldErrors) {
        const fieldErrors = error.response.data.fieldErrors;
        Object.keys(fieldErrors).forEach((field) => {
          setFieldError(field, fieldErrors[field]);
        });
      }

      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <Formik
        initialValues={initialData || initialFormValues}
        validationSchema={gradeValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          //   values,
          //   errors,
          //   touched,
          //   handleChange,
          //   handleBlur,
          isSubmitting,
        }) => (
          <Form>
            <DialogContent sx={{ pt: 3 }}>
              <Box className='space-y-4 md:p-4'>
                <Field name='grade'>
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      label='Grade'
                      placeholder='e.g., A+, A, B+, B'
                      size='small'
                      fullWidth
                      required
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      InputProps={{
                        sx: { textTransform: 'uppercase' },
                      }}
                      onChange={(e) => {
                        const upperValue = e.target.value.toUpperCase();
                        field.onChange({
                          target: { name: field.name, value: upperValue },
                        });
                      }}
                    />
                  )}
                </Field>

                {/* Min Percentage */}

                <Field name='rangeFrom'>
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      label='Min Percentage'
                      type='number'
                      size='small'
                      fullWidth
                      required
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>%</InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Field>

                {/* Max Percentage */}

                <Field name='rangeTo'>
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      label='Max Percentage'
                      type='number'
                      size='small'
                      fullWidth
                      required
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>%</InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Field>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1 }}>
              <Button
                onClick={onClose}
                color='secondary'
                variant='outlined'
                startIcon={<CancelIcon />}
                disabled={isSubmitting || loading}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                color='primary'
                variant='contained'
                startIcon={<SaveIcon />}
                disabled={isSubmitting || loading}
              >
                {isSubmitting || loading
                  ? 'Saving...'
                  : isEditMode
                    ? 'Update'
                    : 'Save'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default GradeFormDialog;
